import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Clock, CheckCircle, XCircle, User, ChevronDown, ChevronUp, 
  Building2, FileText, Image, Globe, Phone, Mail, MapPin, 
  ExternalLink, Download, Shield
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TierBadge from '../components/TierBadge';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Friendly label mapping for form data field keys
const FIELD_LABELS = {
  phone_number: 'Company Phone Number',
  company_legal_name: 'Company Legal Name',
  country_of_incorporation: 'Country of Incorporation',
  company_address: 'Address',
  company_website: 'Website',
  company_type: 'Company Type',
  job_title: 'Job Title',
  preferred_language: 'Language',
  timezone: 'Timezone',
  profile_photo: 'Profile Photo',
  first_name: 'First Name',
  last_name: 'Last Name',
  nationality: 'Nationality',
  tax_id: 'Tax ID',
};

const getFieldLabel = (key) => {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return key.replace(/_/g, ' ');
};

// Helper to detect file URLs and render them as links
const renderFormValue = (key, value) => {
  if (!value || value === '-') return <span>{value || '-'}</span>;
  const strValue = String(value);
  // Detect file/image URLs (stored as /api/files/... paths or full URLs)
  if (strValue.startsWith('/api/files/') || ((strValue.startsWith('http://') || strValue.startsWith('https://')) && /\.(jpg|jpeg|png|gif|webp|svg|pdf|doc|docx)$/i.test(strValue))) {
    const fullUrl = resolveFileUrl(strValue, BACKEND_URL);
    const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(strValue);
    if (isImage) {
      return (
        <div className="mt-1">
          <img src={fullUrl} alt={key} className="w-20 h-20 rounded-lg object-cover border" />
          <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
            View Full Size <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      );
    }
    const filename = strValue.split('/').pop();
    return (
      <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
        <FileText className="w-3 h-3" /> {filename} <ExternalLink className="w-3 h-3" />
      </a>
    );
  }
  return <span>{strValue}</span>;
};

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';
const BRAND_YELLOW = '#FFD166';

const AdminTierApprovals = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [tierStats, setTierStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showDetails, setShowDetails] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pendingRes, statsRes] = await Promise.all([
        api.get('/admin/tiers/pending'),
        api.get('/admin/tiers/stats')
      ]);
      setPendingUsers(pendingRes.data);
      setTierStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load tier data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, tier) => {
    try {
      await api.put(`/admin/tiers/${userId}/approve`, { action: 'approve' });
      toast.success(`${tier.charAt(0).toUpperCase() + tier.slice(1)} tier approved!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (userId, tier) => {
    try {
      await api.put(`/admin/tiers/${userId}/approve`, { 
        action: 'reject',
        rejection_reason: rejectReason || 'Application did not meet requirements'
      });
      toast.success(`${tier.charAt(0).toUpperCase() + tier.slice(1)} tier rejected`);
      setExpandedUser(null);
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleDetails = (userId) => {
    setShowDetails(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Render Bronze verification details
  const renderBronzeDetails = (user) => (
    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
      <h4 className="text-sm font-semibold text-orange-800 mb-3 flex items-center gap-2">
        <User className="w-4 h-4" />
        Bronze Verification - User Profile
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
        <div>
          <span className="text-slate-500">Full Name:</span>
          <p className="font-medium text-slate-800">{user.first_name} {user.last_name}</p>
        </div>
        <div>
          <span className="text-slate-500">Email:</span>
          <p className="font-medium text-slate-800">{user.email}</p>
        </div>
        <div>
          <span className="text-slate-500">Country:</span>
          <p className="font-medium text-slate-800">{user.country || '-'}</p>
        </div>
        <div>
          <span className="text-slate-500">Account Type:</span>
          <p className="font-medium text-slate-800 capitalize">{user.account_type?.replace('_', '/') || '-'}</p>
        </div>
        <div>
          <span className="text-slate-500">Phone:</span>
          <p className="font-medium text-slate-800">{user.phone || '-'}</p>
        </div>
        <div>
          <span className="text-slate-500">WhatsApp:</span>
          <p className="font-medium text-slate-800">{user.whatsapp || '-'}</p>
        </div>
        {user.description && (
          <div className="col-span-2 md:col-span-3">
            <span className="text-slate-500">Description:</span>
            <p className="font-medium text-slate-800">{user.description}</p>
          </div>
        )}
      </div>
      
      {/* Role-specific Question Answers - With proper labels */}
      {user.role_answers_with_labels && Object.keys(user.role_answers_with_labels).length > 0 && (
        <div className="mt-4 pt-4 border-t border-orange-200">
          <h5 className="text-xs font-semibold text-orange-700 uppercase tracking-wider mb-3">
            Role-Specific Answers ({user.account_type || 'User'})
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(user.role_answers_with_labels).map(([questionId, data]) => (
              <div key={questionId} className="bg-white/50 p-3 rounded-lg">
                <span className="text-xs text-slate-500 block mb-1">{data.question_text}</span>
                <p className="font-medium text-slate-800">{data.answer || '-'}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Bronze Tier Documents */}
      {(() => {
        const bronzeDocs = (user.verification_documents || []).filter(doc => doc.tier === 'bronze');
        return bronzeDocs.length > 0 ? (
        <div className="mt-4 pt-4 border-t border-orange-200">
          <h5 className="text-xs font-semibold text-orange-700 uppercase tracking-wider mb-3">
            Bronze Tier Documents ({bronzeDocs.length})
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {bronzeDocs.map((doc, idx) => (
              <div 
                key={doc.id || idx} 
                className="flex items-center gap-3 p-3 bg-white rounded-lg border border-orange-200"
              >
                <FileText className="w-5 h-5 text-orange-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 capitalize">
                    {doc.document_type?.replace(/_/g, ' ') || 'Document'}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{doc.original_filename}</p>
                </div>
                <a
                  href={resolveFileUrl(doc.file_url, BACKEND_URL)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  title="View document"
                >
                  <ExternalLink className="w-4 h-4" />
                  View
                </a>
              </div>
            ))}
          </div>
        </div>
        ) : null;
      })()}

      {/* Tier Submission Data (all form answers) */}
      {user.tier_submissions?.bronze && (
        <div className="mt-4 pt-4 border-t border-orange-200">
          <h5 className="text-xs font-semibold text-orange-700 uppercase tracking-wider mb-3">Complete Form Submission</h5>
          {user.tier_submissions.bronze.form_data && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {Object.entries(user.tier_submissions.bronze.form_data).map(([key, value]) => (
                <div key={key} className="bg-white/50 p-2 rounded">
                  <span className="text-xs text-slate-500 capitalize">{getFieldLabel(key)}:</span>
                  <div className="text-sm font-medium text-slate-800">{renderFormValue(key, value)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render Silver verification details
  const renderSilverDetails = (user) => {
    const company = user.company;
    if (!company) return null;
    
    return (
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          Silver Verification - Company Details
        </h4>
        
        {/* Individual Profile */}
        <div className="mb-4">
          <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Individual Profile</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {company.profile_photo && (
              <div className="col-span-2 md:col-span-1">
                <img 
                  src={resolveFileUrl(company.profile_photo, BACKEND_URL)}
                  alt="Profile" 
                  className="w-16 h-16 rounded-lg object-cover border border-slate-200"
                />
              </div>
            )}
            <div>
              <span className="text-slate-500">Job Title:</span>
              <p className="font-medium text-slate-800">{company.job_title}</p>
            </div>
            <div>
              <span className="text-slate-500">Company Phone Number:</span>
              <p className="font-medium text-slate-800">{company.phone_number}</p>
            </div>
            <div>
              <span className="text-slate-500">Language:</span>
              <p className="font-medium text-slate-800">{company.preferred_language}</p>
            </div>
            <div>
              <span className="text-slate-500">Timezone:</span>
              <p className="font-medium text-slate-800">{company.timezone}</p>
            </div>
          </div>
        </div>
        
        {/* Company Details */}
        <div>
          <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Company / Entity Details</h5>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div className="col-span-2 md:col-span-2">
              <span className="text-slate-500">Company Legal Name:</span>
              <p className="font-medium text-slate-800">{company.company_legal_name}</p>
            </div>
            <div>
              <span className="text-slate-500">Company Type:</span>
              <p className="font-medium text-slate-800">{company.company_type}</p>
            </div>
            <div>
              <span className="text-slate-500">Country of Incorporation:</span>
              <p className="font-medium text-slate-800">{company.country_of_incorporation}</p>
            </div>
            <div className="col-span-2">
              <span className="text-slate-500">Address:</span>
              <p className="font-medium text-slate-800">{company.company_address}</p>
            </div>
            {company.company_website && (
              <div className="col-span-2 md:col-span-3">
                <span className="text-slate-500">Website:</span>
                <a 
                  href={company.company_website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                >
                  {company.company_website} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        </div>
        
        {/* Silver Tier Submission Data */}
        {user.tier_submissions?.silver?.form_data && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Complete Form Submission</h5>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(user.tier_submissions.silver.form_data).map(([key, value]) => {
                // Skip photo URLs - they're already displayed above
                if (key === 'profile_photo') return null;
                return (
                  <div key={key} className="bg-white/50 p-2 rounded">
                    <span className="text-xs text-slate-500 capitalize">{getFieldLabel(key)}:</span>
                    <div className="text-sm font-medium text-slate-800">{renderFormValue(key, value)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Silver Tier Documents */}
        {(() => {
          const silverDocs = (user.verification_documents || []).filter(doc => doc.tier === 'silver');
          return silverDocs.length > 0 ? (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Silver Tier Documents ({silverDocs.length})
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {silverDocs.map((doc, idx) => (
                <div 
                  key={doc.id || idx} 
                  className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200"
                >
                  {doc.content_type?.startsWith('image/') ? (
                    <Image className="w-5 h-5 text-slate-600" />
                  ) : (
                    <FileText className="w-5 h-5 text-slate-600" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 capitalize">
                      {doc.document_type?.replace(/_/g, ' ') || 'Document'}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{doc.original_filename}</p>
                  </div>
                  <a
                    href={resolveFileUrl(doc.file_url, BACKEND_URL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                    title="View document"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View
                  </a>
                </div>
              ))}
            </div>
          </div>
          ) : null;
        })()}
      </div>
    );
  };

  // Render Gold verification details
  const renderGoldDetails = (user) => {
    const docs = (user.verification_documents || []).filter(doc => doc.tier === 'gold');
    
    return (
      <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
        <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4" />
          Gold Verification - KYC/KYB Documents
        </h4>
        
        {/* KYC Info */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-4">
          <div>
            <span className="text-slate-500">Nationality:</span>
            <p className="font-medium text-slate-800">{user.nationality || '-'}</p>
          </div>
          <div>
            <span className="text-slate-500">Tax ID:</span>
            <p className="font-medium text-slate-800">{user.tax_id || '-'}</p>
          </div>
        </div>
        
        {/* Gold Tier Submission Data */}
        {user.tier_submissions?.gold?.form_data && (
          <div className="mb-4 pt-2 border-t border-amber-200">
            <h5 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-3">Form Submission Data</h5>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(user.tier_submissions.gold.form_data).map(([key, value]) => (
                <div key={key} className="bg-white/50 p-2 rounded">
                  <span className="text-xs text-slate-500 capitalize">{getFieldLabel(key)}:</span>
                  <div className="text-sm font-medium text-slate-800">{renderFormValue(key, value)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documents */}
        {docs.length > 0 ? (
          <div className="space-y-2">
            <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Uploaded Documents ({docs.length})
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {docs.map((doc, idx) => (
                <div 
                  key={doc.id || idx} 
                  className="flex items-center gap-3 p-3 bg-white rounded-lg border border-amber-200"
                >
                  {doc.content_type?.startsWith('image/') ? (
                    <Image className="w-5 h-5 text-amber-600" />
                  ) : (
                    <FileText className="w-5 h-5 text-amber-600" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 capitalize">
                      {doc.document_type?.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{doc.original_filename}</p>
                  </div>
                  <a
                    href={resolveFileUrl(doc.file_url, BACKEND_URL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                    title="View document"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View
                  </a>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-amber-600">No documents uploaded</p>
        )}
      </div>
    );
  };

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Tier Approvals</h1>
          <p className="text-gray-500 text-sm mt-1">Review and approve user verification requests</p>
        </div>

        {/* Stats Cards */}
        {tierStats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5" style={{ color: BRAND_ORANGE }} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-xl font-semibold" style={{ color: BRAND_ORANGE }}>{tierStats.pending_verifications}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Unverified</p>
                  <p className="text-xl font-semibold text-gray-900">{tierStats.by_tier?.none || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-orange-200 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Bronze</p>
                  <p className="text-xl font-semibold text-orange-600">{tierStats.by_tier?.bronze || 0}</p>
                  {tierStats.pending_by_tier?.bronze > 0 && (
                    <p className="text-xs text-orange-400">+{tierStats.pending_by_tier.bronze} pending</p>
                  )}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Silver</p>
                  <p className="text-xl font-semibold text-gray-700">{tierStats.by_tier?.silver || 0}</p>
                  {tierStats.pending_by_tier?.silver > 0 && (
                    <p className="text-xs text-gray-400">+{tierStats.pending_by_tier.silver} pending</p>
                  )}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-amber-200 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Gold</p>
                  <p className="text-xl font-semibold text-amber-600">{tierStats.by_tier?.gold || 0}</p>
                  {tierStats.pending_by_tier?.gold > 0 && (
                    <p className="text-xs text-amber-400">+{tierStats.pending_by_tier.gold} pending</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending Approvals */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4" style={{ color: BRAND_ORANGE }} />
            </div>
            <h2 className="font-medium text-gray-900">Pending Verifications</h2>
            <span 
              className="ml-auto px-2.5 py-1 rounded-full text-xs font-medium text-white"
              style={{ backgroundColor: BRAND_ORANGE }}
            >
              {pendingUsers.length}
            </span>
          </div>

          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 mx-auto mb-3" style={{ borderTopColor: BRAND_BLUE }}></div>
              Loading...
            </div>
          ) : pendingUsers.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-green-500" />
              </div>
              <p className="text-gray-600 font-medium">No pending verifications</p>
              <p className="text-gray-400 text-sm mt-1">All caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {pendingUsers.map((user) => (
                <div key={user.id} className="p-5" data-testid={`pending-user-${user.id}`}>
                  {/* User Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden">
                        {user.profile_photo ? (
                          <img src={resolveFileUrl(user.profile_photo, BACKEND_URL)} alt="" className="w-12 h-12 rounded-xl object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}`
                              : user.email}
                          </span>
                          <TierBadge tier={user.tier} tierStatus="pending" size="sm" />
                        </div>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Submitted: {formatDate(user[`${user.tier}_submitted_at`])}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleDetails(user.id)}
                        className="flex items-center gap-1 px-3 py-2 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition text-sm"
                      >
                        {showDetails[user.id] ? 'Hide' : 'View'} Details
                        {showDetails[user.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleApprove(user.id, user.tier)}
                        className="flex items-center gap-1 px-4 py-2 text-white rounded-xl transition text-sm font-medium"
                        style={{ backgroundColor: BRAND_BLUE }}
                        data-testid={`approve-${user.id}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
                        className="flex items-center gap-1 px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition text-sm font-medium"
                        data-testid={`reject-btn-${user.id}`}
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>

                  {/* Verification Details */}
                  {showDetails[user.id] && (
                    <div className="mt-4 space-y-4">
                      {user.tier === 'bronze' && renderBronzeDetails(user)}
                      {user.tier === 'silver' && (
                        <>
                          {renderBronzeDetails(user)}
                          {renderSilverDetails(user)}
                        </>
                      )}
                      {user.tier === 'gold' && (
                        <>
                          {renderBronzeDetails(user)}
                          {renderSilverDetails(user)}
                          {renderGoldDetails(user)}
                        </>
                      )}
                    </div>
                  )}

                  {/* Rejection Form (expandable) */}
                  {expandedUser === user.id && (
                    <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-100">
                      <label className="block text-sm font-medium text-red-800 mb-2">
                        Rejection Reason
                      </label>
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Enter reason for rejection (optional)"
                        rows={2}
                        className="w-full px-3 py-2 border border-red-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                        data-testid={`reject-reason-${user.id}`}
                      />
                      <div className="flex justify-end gap-2 mt-3">
                        <button
                          onClick={() => { setExpandedUser(null); setRejectReason(''); }}
                          className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleReject(user.id, user.tier)}
                          className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition text-sm font-medium"
                          data-testid={`confirm-reject-${user.id}`}
                        >
                          Confirm Rejection
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminTierApprovals;
